import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { currency } from "@/lib/status";
import { PROMO_TYPES, promoBadge, promoMatchesVehicle, daysLeft, scopeLabel } from "@/lib/promotions";
import { OfferCountdown } from "@/components/OfferCountdown";
import { useContent, SPECIALS_DEFAULTS } from "@/lib/content";
import { SiteFooter } from "@/components/SiteFooter";
import { BrandMark } from "@/components/BrandMark";
import { PromotionBannerCarousel } from "@/components/PromotionBannerCarousel";
import { Tag, ArrowRight, CalendarDays, Car, ShieldCheck } from "lucide-react";

export default function Specials() {
  const navigate = useNavigate();
  const c = useContent("specials", SPECIALS_DEFAULTS);
  const [promos, setPromos] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [typeF, setTypeF] = useState("all");
  const [makeF, setMakeF] = useState("all");
  const [sort, setSort] = useState("priority");

  useEffect(() => {
    Promise.all([api.get("/promotions/active"), api.get("/vehicles")])
      .then(([p, v]) => {
        const list = p.data || [];
        setPromos(list); setVehicles(v.data || []);
        list
          .filter((x) => x.display_format !== "banner")
          .forEach((x) => api.post(`/promotions/${x.id}/view`).catch(() => {}));
      })
      .catch(() => setPromos([]));
  }, []);

  const go = (p) => {
    api.post(`/promotions/${p.id}/click`).catch(() => {});
    navigate(p.scope === "manufacturer" && p.make ? `/inventory?make=${encodeURIComponent(p.make)}` : "/inventory");
  };

  const detail = (p) => {
    const bits = [];
    if (p.apr != null) bits.push(`${p.apr}% APR`);
    if (p.monthly_payment != null) bits.push(`${currency(p.monthly_payment)}/mo`);
    if (p.down_payment != null) bits.push(`${currency(p.down_payment)} down`);
    if (p.term_months != null) bits.push(`${p.term_months} mo`);
    if (p.cash_amount != null) bits.push(`${currency(p.cash_amount)} cash back`);
    return bits.join(" · ");
  };

  const list = (promos || []).filter((p) => p.display_format !== "banner");
  const banners = (promos || []).filter((p) => p.display_format === "banner");
  const types = [...new Set(list.map((p) => p.promo_type))];
  const makes = [...new Set(list.filter((p) => p.scope === "manufacturer" && p.make).map((p) => p.make))];
  const filtered = list.filter((p) => (typeF === "all" || p.promo_type === typeF) && (makeF === "all" || p.make === makeF));
  const sorted = [...filtered].sort((a, b) => {
    if (sort === "ending") return (daysLeft(a) ?? 9999) - (daysLeft(b) ?? 9999);
    if (sort === "newest") return String(b.created_at || "").localeCompare(String(a.created_at || ""));
    return (b.priority || 0) - (a.priority || 0);
  });
  const chip = (on) => `rounded-full border px-3 py-1.5 text-sm font-bold transition-colors ${on ? "border-rose-600 bg-rose-600 text-white" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:border-slate-300"}`;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #e11d48 0, transparent 40%)" }} />
        <div className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-rose-400" data-testid="specials-eyebrow">{c.specials_eyebrow}</p>
          <h1 className="mt-2 font-display text-4xl font-black uppercase tracking-tight sm:text-5xl" data-testid="specials-heading">{c.specials_heading}</h1>
          <p className="mt-3 max-w-2xl text-slate-300" data-testid="specials-subtext">{c.specials_subtext}</p>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <PromotionBannerCarousel promotions={banners} />
        {list.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="specials-filters">
            <button onClick={() => setTypeF("all")} className={chip(typeF === "all")} data-testid="specials-type-all">All types</button>
            {types.map((t) => <button key={t} onClick={() => setTypeF(t)} className={chip(typeF === t)} data-testid={`specials-type-${t}`}>{PROMO_TYPES[t]?.label || t}</button>)}
            {makes.length > 0 && <span className="mx-1 hidden h-5 w-px bg-slate-300 dark:bg-slate-700 sm:inline-block" />}
            {makes.map((m) => <button key={m} onClick={() => setMakeF(makeF === m ? "all" : m)} className={chip(makeF === m)} data-testid={`specials-make-${m}`}>{m}</button>)}
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="specials-sort" className="ml-auto rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300">
              <option value="priority">Featured</option>
              <option value="ending">Ending soon</option>
              <option value="newest">Newest</option>
            </select>
          </div>
        )}
        {promos === null ? (
          <p className="text-slate-400">Loading specials…</p>
        ) : promos.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-800 py-20 text-center" data-testid="specials-empty">
            <Tag className="mx-auto h-9 w-9 text-slate-300" />
            <p className="mt-3 font-display text-lg font-bold text-slate-700 dark:text-slate-200">{c.specials_empty_title}</p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{c.specials_empty_subtext}</p>
            <Button onClick={() => navigate("/concierge")} className="mt-5 rounded-md bg-rose-600 font-bold text-white hover:bg-rose-700">{c.specials_empty_cta}</Button>
          </div>
        ) : sorted.length === 0 ? (
          <div className="py-16 text-center text-slate-500 dark:text-slate-400" data-testid="specials-no-match">No specials match these filters.</div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2" data-testid="specials-grid">
            {sorted.map((p) => {
              const matching = vehicles.filter((v) => promoMatchesVehicle(p, v));
              const meta = PROMO_TYPES[p.promo_type] || {};
              return (
                <div key={p.id} data-testid={`special-card-${p.id}`} className="flex flex-col overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-red-400 hover:shadow-xl hover:shadow-red-600/10">
                  <div className={`flex items-center justify-between px-5 py-3 ${meta.cls || "bg-rose-600 text-white"}`}>
                    <span className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide"><Tag className="h-4 w-4" strokeWidth={2.5} /> {promoBadge(p)}</span>
                    {(p.start_date || p.end_date) && <span className="inline-flex items-center gap-1 text-xs font-semibold opacity-90"><CalendarDays className="h-3.5 w-3.5" /> {p.end_date ? `ends ${p.end_date}` : `from ${p.start_date}`}</span>}
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex items-start gap-3">
                      <BrandMark make={p.make} logoUrl={p.logo_url} />
                      <div className="min-w-0">
                        {scopeLabel(p) && <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">{scopeLabel(p)}</p>}
                        {detail(p) ? (
                          <>
                            <p data-testid={`special-headline-${p.id}`} className="font-display text-2xl sm:text-3xl font-black leading-none tracking-tight break-words text-red-600 dark:text-red-500">{detail(p)}</p>
                            <h3 className="mt-1.5 font-display text-base font-bold text-slate-700 dark:text-slate-200">{p.title}</h3>
                          </>
                        ) : (
                          <h3 data-testid={`special-headline-${p.id}`} className="font-display text-2xl sm:text-3xl font-black leading-none tracking-tight break-words text-red-600 dark:text-red-500">{p.title}</h3>
                        )}
                      </div>
                    </div>
                    <OfferCountdown promo={p} className="mt-3 self-start" testId={`special-expiry-${p.id}`} />
                    {p.description && <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{p.description}</p>}
                    {p.terms && <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">{p.terms}</p>}
                    <div className="mt-4 flex flex-wrap items-center gap-3 text-xs font-semibold text-slate-500 dark:text-slate-400">
                      <span className="inline-flex items-center gap-1.5"><Car className="h-3.5 w-3.5" /> {matching.length} matching vehicle{matching.length !== 1 ? "s" : ""} in stock</span>
                      <span className="inline-flex items-center gap-1 text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" /> Broker-verified</span>
                    </div>
                    <div className="mt-auto pt-5">
                      <Button onClick={() => go(p)} data-testid={`special-view-${p.id}`} className="w-full rounded-md bg-slate-950 font-bold text-white hover:bg-slate-800 dark:bg-rose-600 dark:hover:bg-rose-700">
                        View matching cars <ArrowRight className="ml-1.5 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
